import { AxiosError, AxiosResponse } from "axios";

import { PurchasableProducts } from "@/pages/shopAll/types";
import {
  AddressInfo,
  Consult,
  ConsultIntake,
  ConsultRecommendedProducts,
  FAQ,
  Period,
  PharmacyResultType,
  Prescription,
  TreatmentPlan,
} from "@/types/care";
import {
  deleteRequest,
  getRequest,
  patchRequest,
  postRequest,
  putRequest,
} from "@/utils/axios";

type SuccessCallback<T = any> = (
  response: AxiosResponse<T>
) => AxiosResponse<T>;
type ErrorCallback = (error: AxiosError, response?: any) => void;

function fetchConsults(
  onSuccess?: SuccessCallback<Consult[]>,
  onError?: ErrorCallback
): Promise<AxiosResponse<Consult[]> | void> {
  return getRequest(
    "/api/v1/consults/",
    (response: AxiosResponse<Consult[]>) => {
      if (onSuccess) {
        onSuccess(response);
      }
      return response;
    },
    (error: AxiosError, response?: any) => {
      if (onError) {
        onError(error, response);
      }
    }
  );
}

function createConsult(
  data: any,
  onSuccess?: SuccessCallback<Consult>,
  onError?: ErrorCallback
): Promise<AxiosResponse<Consult> | void> {
  return postRequest(
    "/api/v1/consults/",
    data,
    (response: AxiosResponse<Consult>) => {
      if (onSuccess) {
        onSuccess(response);
      }
      return response;
    },
    (error: AxiosError, response?: any) => {
      if (onError) {
        onError(error, response);
      }
    }
  );
}

async function asyncCreateConsult(data: any): Promise<AxiosResponse<Consult>> {
  const response = await postRequest(
    "/api/v1/consults/",
    data,
    (response: AxiosResponse<Consult>) => {
      return response;
    },
    (error: AxiosError, response?: any) => {
      console.error(error);
      throw error;
    }
  );
  return response;
}

function fetchTreatmentPlan(
  hash: string
): Promise<AxiosResponse<TreatmentPlan>> {
  return getRequest(
    `/api/v1/consults/${hash}/treatment_plan/`,
    (response: AxiosResponse<TreatmentPlan>) => {
      return response;
    },
    (error: AxiosError) => {
      throw error;
    }
  );
}

function setTreatmentPlanStartDate(
  hash: string,
  data: { start_date: string }
): Promise<AxiosResponse<any>> {
  return postRequest(
    `/api/v1/consults/${hash}/treatment_start_date/`,
    data,
    (response: AxiosResponse<any>) => {
      return response;
    },
    (error: AxiosError) => {
      throw error;
    }
  );
}

async function createPeriod({
  consultUid,
  startDate,
  days,
}: {
  consultUid: string;
  startDate: string;
  days: number;
}): Promise<AxiosResponse<Period>> {
  const data = {
    consult_uid: consultUid,
    start_date: startDate,
    days,
  };
  const response = await postRequest(
    `/api/v1/treatment-interruption/`,
    data,
    (response: AxiosResponse<Period>) => {
      return response;
    },
    (error: AxiosError, response?: any) => {
      console.error(error);
      throw error;
    }
  );
  return response;
}

async function updatePeriod({
  id,
  startDate,
  days,
}: {
  id: string;
  startDate: string;
  days: number;
}): Promise<AxiosResponse<Period>> {
  const data = {
    start_date: startDate,
    days,
  };
  const response = await putRequest(
    `/api/v1/treatment-interruption/${id}/`,
    data,
    (response: AxiosResponse<Period>) => {
      return response;
    },
    (error: AxiosError, response?: any) => {
      console.error(error);
      throw error;
    }
  );
  return response;
}

async function deletePeriod(id: string): Promise<AxiosResponse<any>> {
  const response = await deleteRequest(
    `/api/v1/treatment-interruption/${id}/`,
    (response: AxiosResponse<any>) => {
      return response;
    },
    (error: AxiosError, response?: any) => {
      console.error(error);
      throw error;
    }
  );
  return response;
}

function fetchConsult(
  consultUid: string,
  onSuccess?: SuccessCallback<Consult>,
  onError?: ErrorCallback
): Promise<AxiosResponse<Consult> | void> {
  return getRequest(
    `/api/v1/consults/${consultUid}/`,
    (response: AxiosResponse<Consult>) => {
      if (onSuccess) {
        onSuccess(response);
      }
      return response;
    },
    (error: AxiosError, response?: any) => {
      if (onError) {
        onError(error, response);
      }
    }
  );
}

function fetchConsultMessages(
  consultUid: string,
  limit?: number,
  before?: string,
  onSuccess?: SuccessCallback<any>,
  onError?: ErrorCallback
): Promise<AxiosResponse<any> | void> {
  var url = `/api/v1/consults/${consultUid}/messages/`;
  if (!limit) {
    limit = 100;
  }
  url += `?limit=${limit}`;
  if (before) {
    url += `&before=${before}`;
  }
  return getRequest(
    url,
    (response: AxiosResponse<any>) => {
      if (onSuccess) {
        onSuccess(response);
      }
      return response;
    },
    (error: AxiosError, response?: any) => {
      if (onError) {
        onError(error, response);
      }
    }
  );
}

function sendConsultMessage(
  consultUid: string,
  data: any,
  onSuccess?: SuccessCallback<any>,
  onError?: ErrorCallback
): Promise<AxiosResponse<any> | void> {
  return postRequest(
    `/api/v1/consults/${consultUid}/messages/`,
    data,
    (response: AxiosResponse<any>) => {
      if (onSuccess) {
        onSuccess(response);
      }
    },
    (error: AxiosError, response?: any) => {
      if (onError) {
        onError(error, response);
      }
    }
  );
}

function getAssociatedConsultForOrder(
  params: any,
  onSuccess?: SuccessCallback<Consult>,
  onError?: ErrorCallback
) {
  // Support both old signature (checkoutId as string) and new signature (params object)
  let queryParams = "";
  if (typeof params === "string") {
    // Backward compatibility: treat as checkoutId
    queryParams = `?checkout_id=${encodeURIComponent(params)}`;
  } else if (params && typeof params === "object") {
    // New signature: params object with checkout_id and/or checkout_token
    const urlParams = new URLSearchParams();
    if (params.checkout_id) {
      urlParams.append("checkout_id", params.checkout_id);
    }
    if (params.checkout_token) {
      urlParams.append("checkout_token", params.checkout_token);
    }
    queryParams = urlParams.toString() ? `?${urlParams.toString()}` : "";
  }

  return getRequest(
    `/api/v1/consults/order/${queryParams}`,
    (response: AxiosResponse<Consult>) => {
      if (onSuccess) {
        onSuccess(response);
      }
    },
    (error: AxiosError) => {
      if (onError) {
        throw error;
      }
    }
  );
}

function fetchConsultCheckoutURL(
  consultUid: string,
  onSuccess?: SuccessCallback<{ checkout_url: string }>,
  onError?: ErrorCallback
): Promise<AxiosResponse<{ checkout_url: string }> | void> {
  return getRequest(
    `/api/v1/consults/${consultUid}/checkout_url/`,
    (response: AxiosResponse<{ checkout_url: string }>) => {
      if (onSuccess) {
        onSuccess(response);
      }
      return response;
    },
    (error: AxiosError, response?: any) => {
      if (onError) {
        onError(error, response);
      }
    }
  );
}

async function createConsultUpsellCheckoutUrl({
  consultUid,
  data,
}: {
  consultUid: string;
  data: any;
}): Promise<AxiosResponse<{ checkout_url: string }>> {
  const response = await postRequest(
    `/api/v1/consults/${consultUid}/cross-sell-checkout-url/`,
    data,
    (response: AxiosResponse<{ checkout_url: string }>) => {
      return response;
    },
    (error: AxiosError) => {
      throw error;
    }
  );
  return response;
}

type ConsultAdditionalProducts = {
  a_la_care_treatments: ConsultRecommendedProducts["aLaCareTreatments"];
  ungated_rx_treatments: ConsultRecommendedProducts["ungatedRxTreatments"];
  test: ConsultRecommendedProducts["test"];
};

async function fetchConsultAdditionalProducts(
  consultUid: string,
  onSuccess?: SuccessCallback<ConsultAdditionalProducts>,
  onError?: ErrorCallback
): Promise<AxiosResponse<ConsultAdditionalProducts> | void> {
  return getRequest(
    `/api/v1/consults/${consultUid}/recommended-products/`,
    (response: AxiosResponse<ConsultAdditionalProducts>) => {
      if (onSuccess) {
        onSuccess(response);
      }
      return response;
    },
    (error: AxiosError, response?: any) => {
      if (onError) {
        onError(error, response);
      }
      return response;
    }
  );
}

function fetchConsultIntake(
  consultUid: string,
  onSuccess?: SuccessCallback<ConsultIntake>,
  onError?: ErrorCallback
): Promise<AxiosResponse<ConsultIntake> | void> {
  return getRequest(
    `/api/v1/consult-intake/${consultUid}/`,
    (response: AxiosResponse<ConsultIntake>) => {
      if (onSuccess) {
        return onSuccess(response);
      }
    },
    (error: AxiosError, response?: any) => {
      if (onError) {
        return onError(error, response);
      }
    }
  );
}

function createConsultIntake(
  data: any,
  onSuccess?: SuccessCallback<ConsultIntake>,
  onError?: ErrorCallback
): Promise<AxiosResponse<ConsultIntake> | void> {
  return postRequest(
    "/api/v1/consult-intake/",
    data,
    (response: AxiosResponse<ConsultIntake>) => {
      if (onSuccess) {
        return onSuccess(response);
      }
      return response;
    },
    (error: AxiosError, response?: any) => {
      if (onError) {
        return onError(error, response);
      }
    }
  );
}

async function asyncAgreeToTerms(
  consultUid: string
): Promise<AxiosResponse<any>> {
  const response = await postRequest(
    `/api/v1/consult-intake/${consultUid}/agree_to_terms/`,
    {},
    (response: AxiosResponse<any>) => {
      return response;
    },
    (error: AxiosError) => {
      throw error;
    }
  );
  return response;
}

async function asyncConsentToUngatedRxTerms(
  consultUid: string
): Promise<AxiosResponse<any>> {
  const response = await postRequest(
    `/api/v1/consult-intake/${consultUid}/consent_to_ungated_rx_terms/`,
    {},
    (response: AxiosResponse<any>) => {
      return response;
    },
    (error: AxiosError) => {
      throw error;
    }
  );
  return response;
}

async function asyncConsentToTryingNotToConceive(
  consultUid: string
): Promise<AxiosResponse<any>> {
  const response = await postRequest(
    `/api/v1/consult-intake/${consultUid}/consent_to_not_trying_to_conceive/`,
    {},
    (response: AxiosResponse<any>) => {
      return response;
    },
    (error: AxiosError) => {
      throw error;
    }
  );
  return response;
}

async function updateConsultPharmacyType(
  consultUid: string,
  pharmacyType: string
): Promise<AxiosResponse<Consult>> {
  const response = await putRequest(
    `/api/v1/consults/${consultUid}/`,
    { pharmacy_type: pharmacyType },
    (response: AxiosResponse<Consult>) => {
      return response;
    },
    (error: AxiosError) => {
      throw error;
    }
  );
  return response;
}

async function updateConsultIntake(
  consultUid: string,
  data: any
): Promise<AxiosResponse<ConsultIntake>> {
  try {
    const response = await patchRequest(
      `/api/v1/consult-intake/${consultUid}/`,
      data,
      (response: AxiosResponse<ConsultIntake>) => {
        return response;
      },
      (error: AxiosError, response?: any) => {
        console.error(error);
        throw error;
      }
    );

    return response;
  } catch (error: any) {
    // Re-throw with both error and response data for compatibility
    const errorData = {
      error: error,
      resp: error.response?.data || error.message,
    };
    throw errorData;
  }
}

async function submitConsultIntake(
  consultUid: string
): Promise<AxiosResponse<any>> {
  try {
    const response = await postRequest(
      `/api/v1/consult-intake/${consultUid}/submit/`,
      {},
      (response: AxiosResponse<any>) => {
        return response;
      },
      (error: AxiosError, response?: any) => {
        console.error(error);
        const errorData = {
          error: error,
          resp: response || error.message,
        };
        throw errorData;
      }
    );
    return response;
  } catch (error: any) {
    console.error(error);
    throw error;
  }
}

function fetchMedications(
  onSuccess?: SuccessCallback<Prescription[]>,
  onError?: ErrorCallback
): Promise<AxiosResponse<Prescription[]> | void> {
  return getRequest(
    `/api/v1/consult-intake/medications/`,
    (response: AxiosResponse<Prescription[]>) => {
      if (onSuccess) {
        onSuccess(response);
      }
      return response;
    },
    (error: AxiosError, response?: any) => {
      if (onError) {
        onError(error, response);
      }
    }
  );
}

async function asyncSearchPharmacies(
  postalCode?: string,
  pharmacyName?: string
): Promise<AxiosResponse<PharmacyResultType[]>> {
  let queryParams: string[] = [];
  if (postalCode) {
    queryParams.push(`postal_code=${postalCode}`);
  }

  if (pharmacyName) {
    queryParams.push(`pharmacy_name=${pharmacyName}`);
  }
  const url = `/api/v1/consult-intake/pharmacies/?${queryParams.join("&")}`;

  const response = await getRequest(
    url,
    (response: AxiosResponse<PharmacyResultType[]>) => {
      return response;
    },
    (error: AxiosError) => {
      throw error;
    }
  );
  return response;
}

async function asyncFetchTreatmentAdjustmentReasons(
  adjustmentType: string
): Promise<AxiosResponse<any>> {
  const response = await getRequest(
    `/api/v1/calendar-treatments/adjustment-reason-options/?adjustment_type=${adjustmentType}`,
    (response: AxiosResponse<any>) => {
      return response;
    },
    (error: AxiosError) => {
      throw error;
    }
  );
  return response;
}

async function asyncUpdateCalendarTreatment(
  treatmentId: string,
  data: any
): Promise<AxiosResponse<any>> {
  const response = await patchRequest(
    `/api/v1/calendar-treatments/${treatmentId}/`,
    data,
    (response: AxiosResponse<any>) => {
      return response;
    },
    (error: AxiosError) => {
      throw error;
    }
  );
  return response;
}

async function resetCalendarToDefault(
  consultUid: string
): Promise<AxiosResponse<any>> {
  const response = await patchRequest(
    `/api/v1/consults/${consultUid}/reset-calendar/`,
    {},
    (response: AxiosResponse<any>) => {
      return response;
    },
    (error: AxiosError) => {
      throw error;
    }
  );
  return response;
}

async function asyncFetchTreatmentProductInfo(
  treatmentSlug: string
): Promise<AxiosResponse<Prescription>> {
  const response = await getRequest(
    `/api/v1/products/${treatmentSlug}/`,
    (response: AxiosResponse<Prescription>) => {
      return response;
    },
    (error: AxiosError) => {
      throw error;
    }
  );
  return response;
}

const fetchFAQs = async (
  page: string
): Promise<AxiosResponse<{ faqs: FAQ[] }>> => {
  const response = await getRequest(
    `/api/v1/faqs/?page=${page}`,
    (response: AxiosResponse<{ faqs: FAQ[] }>) => {
      return response;
    },
    (error: AxiosError) => {
      throw error;
    }
  );
  return response;
};

async function asyncFetchConsultAddressInfo(
  consultUid: string
): Promise<AxiosResponse<AddressInfo>> {
  const response = await getRequest(
    `/api/v1/consult-intake/${consultUid}/address-info/`,
    (response: AxiosResponse<AddressInfo>) => {
      return response;
    },
    (error: AxiosError) => {
      throw error;
    }
  );
  return response;
}

async function asyncFetchPurchasableProducts(): Promise<
  AxiosResponse<PurchasableProducts>
> {
  const response = await getRequest(
    "/api/v1/purchasable-products/",
    (response: AxiosResponse<PurchasableProducts>) => {
      return response;
    },
    (error: AxiosError) => {
      throw error;
    }
  );
  return response;
}

export const careService = {
  asyncCreateConsult,
  fetchConsults,
  fetchConsult,
  createConsult,
  fetchConsultCheckoutURL,
  fetchTreatmentPlan,
  fetchMedications,
  setTreatmentPlanStartDate,
  fetchConsultIntake,
  createConsultIntake,
  updateConsultIntake,
  submitConsultIntake,
  fetchConsultMessages,
  sendConsultMessage,
  createPeriod,
  updatePeriod,
  deletePeriod,
  asyncAgreeToTerms,
  asyncConsentToUngatedRxTerms,
  asyncConsentToTryingNotToConceive,
  updateConsultPharmacyType,
  asyncSearchPharmacies,
  asyncFetchTreatmentAdjustmentReasons,
  asyncUpdateCalendarTreatment,
  resetCalendarToDefault,
  asyncFetchTreatmentProductInfo,
  createConsultUpsellCheckoutUrl,
  getAssociatedConsultForOrder,
  fetchConsultAdditionalProducts,
  asyncFetchConsultAddressInfo,
  fetchFAQs,
  asyncFetchPurchasableProducts,
};
